<template>
    <pendo-popover-v2
        v-if="hasOptions"
        ref="pendoToolsPopover"
        :title="$t('pendoTools.Pendo tools')"
        placement="bottom-end"
        :hover="false"
        :popper-class="['pendo-tools__popover', 'top-nav__popover']"
        keyboard-navigation
        @show="focusFirstOption"
        @hide="$emit('select')">
        <template #default="{ shown, ...triggerAttrs }">
            <button
                v-pendo-tooltip="{
                    content: $t('pendoTools.Pendo tools'),
                    arrow: true,
                }"
                v-bind="triggerAttrs"
                class="top-nav__trigger top-nav__button-trigger"
                :class="{
                    'is-open': shown
                }"
                data-cy="pendo-tools-dropdown-trigger"
                pendo-analytics="pendo-tools-nav-item"
                :aria-label="$t('pendoTools.Pendo tools')"
                @keydown.tab="handleTabKeydown($event, shown, triggerAttrs)">
                <pendo-tools-icon />
            </button>
        </template>
        <template #popover>
            <div
                ref="pendoToolsContent"
                class="top-nav__popover-content"
                data-cy="pendo-tools-dropdown-content">
                <h2 class="nav-title">
                    {{ $t('pendoTools.Pendo tools (internal)') }}
                </h2>
                <pendo-router-link
                    v-for="option in filteredOptions"
                    :key="option.id"
                    tabindex="0"
                    class="nav-option"
                    :aria-label="option.label"
                    :pendo-analytics="option.pendoAnalytics"
                    :to="option.route"
                    :target="option.target || '_self'"
                    :external="option.external"
                    @click="handleOptionClick($event)"
                    @keydown.space="handleOptionKeydown($event)"
                    @keydown.enter="handleOptionKeydown($event)"
                    @keydown.tab.prevent="handleOptionKeydown($event)">
                    <div
                        v-pendo-tooltip="{
                            content: tooltipContent,
                            arrow: true,
                            placement: 'top',
                            delay: { show: 1000, hide: 0 },
                        }"
                        class="nav-option__text"
                        @mouseover="onMouseover($event, option)">
                        {{ option.label }}
                    </div>
                </pendo-router-link>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { PendoTooltip, PendoPopoverV2 } from '@pendo/components';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import PendoToolsIcon from '@/top-nav/components/icons/pendo-tools-icon.vue';

import {
    hasJobsAndAggsRedirect,
    hasSuperAccessRedirect,
    hasSuperSubsListRedirect,
    hasSuperToolsRedirect,
} from '@/vue-router/route-permissions.utils';
import { getWatcherBucket } from '@/settings/environment.utils';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils.js';
import { elementHasOverflowX } from '@/utils/dom.utils';

export default {
    name: 'PendoToolsDropdown',
    components: {
        PendoPopoverV2,
        PendoRouterLink,
        PendoToolsIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select'],
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        toolOptions () {
            const keyboardShortcut = '⌘ + \\';

            return [
                {
                    id: 'subscriptions',
                    label: !hasSuperAccessRedirect()
                        ? this.$t('pendoTools.Subscriptions ({keyboardShortcut})', { keyboardShortcut })
                        : this.$t('pendoTools.Subscriptions'),
                    route: { name: 'Subscriptions' },
                    pendoAnalytics: 'super-subscriptions',
                    hasPermissions: () => !hasSuperSubsListRedirect(),
                },
                {
                    id: 'jobs',
                    label: this.$t('pendoTools.Job console (Ops)'),
                    route: { name: 'OpsJobs' },
                    pendoAnalytics: 'super-job-console',
                    hasPermissions: () => !hasJobsAndAggsRedirect(),
                },
                {
                    id: 'aggs',
                    label: this.$t('pendoTools.Aggregations (Ops)'),
                    route: { name: 'Aggregations' },
                    pendoAnalytics: 'super-aggregations',
                    hasPermissions: () => !hasJobsAndAggsRedirect(),
                },
                {
                    id: 'agg-builder',
                    label: this.$t('pendoTools.Agg builder'),
                    route: { name: 'AggregationsBuilder' },
                    pendoAnalytics: 'super-agg-builder',
                    hasPermissions: () => !hasSuperAccessRedirect(),
                },
                {
                    id: 'super-tools',
                    label: this.$t('pendoTools.Super tools'),
                    route: { name: 'SuperTools' },
                    pendoAnalytics: 'super-tools',
                    hasPermissions: () => !hasSuperToolsRedirect(),
                },
                {
                    id: 'watcher',
                    label: this.$t('pendoTools.Watcher'),
                    route: getWatcherBucket(),
                    pendoAnalytics: 'super-watcher',
                    hasPermissions: () => !hasSuperAccessRedirect(),
                    target: '_blank',
                    external: true,
                },
            ];
        },
        filteredOptions () {
            return this.toolOptions.filter((option) => {
                if (!option.hasPermissions) {
                    return true;
                }

                return option.hasPermissions();
            });
        },
        hasOptions () {
            return this.filteredOptions.length > 0;
        },
    },
    methods: {
        handleOptionClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            this.handleOptionSelect();
        },
        handleOptionKeydown ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            $event.preventDefault();
            $event.currentTarget.click();
        },
        handleOptionSelect () {
            this.hide();
            this.$emit('select');
        },
        hide () {
            this.$refs.pendoToolsPopover?.hide?.();
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.pendoToolsContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        onMouseover ({ currentTarget }, option) {
            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = option.label;
            } else {
                this.tooltipContent = null;
            }
        },
        handleTabKeydown ($event, shown, triggerAttrs) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                if (shown) {
                    triggerAttrs.hide();
                } else {
                    triggerAttrs.show();
                }
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

:deep(.pendo-tools-icon) {
    height: 24px;
    width: 24px;
}
</style>

<style lang="less">
.top-nav__popover.pendo-tools__popover {
    .pendo-popover-v2__panel {
        width: 210px;
        padding: 0;
    }
}
</style>
